import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, RefreshCw, Loader2, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "aic_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Login = ({ onAuth }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(KEY, data.access_token);
      onAuth(data.access_token);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-brand-surface px-6">
      <motion.form
        data-testid="admin-login-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-[2rem] bg-white border border-brand-line p-8 shadow-[0_30px_80px_-40px_rgba(4,47,46,0.35)]"
      >
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-icon.png" alt="Aid In Clean" className="h-11 w-auto" />
          <span className="text-lg font-semibold text-brand-ink">Admin</span>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl bg-brand-surface border-brand-line focus-visible:ring-brand-primary" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl bg-brand-surface border-brand-line focus-visible:ring-brand-primary" />
          </div>
          {err && <p data-testid="admin-login-error" className="text-sm text-red-600">{err}</p>}
          <button data-testid="admin-login-submit" disabled={loading} className="w-full flex items-center justify-center rounded-full bg-brand-primary text-white font-medium py-3.5 hover:bg-brand-primaryhover transition-colors disabled:opacity-70">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-brand-surface">
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-brand-line">
        <div className="max-w-screen-xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="Aid In Clean" className="h-10 w-auto" />
            <div>
              <div className="font-semibold text-brand-ink leading-tight">Leads Dashboard</div>
              <div className="text-xs text-brand-ink2">Aid In Clean</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="grid place-items-center h-10 w-10 rounded-xl bg-brand-surface text-brand-ink hover:bg-brand-surface2 transition-colors"><RefreshCw className="h-4 w-4" /></button>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 rounded-full bg-brand-ink text-white text-sm font-medium px-4 py-2.5"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-brand-ink">Quote requests <span className="text-brand-primary">({quotes.length})</span></h1>
        </div>

        {loading ? (
          <div className="grid place-items-center py-24 text-brand-ink2"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : quotes.length === 0 ? (
          <div data-testid="admin-empty" className="grid place-items-center py-24 text-brand-ink2 gap-3">
            <Inbox className="h-10 w-10" /> No quote requests yet.
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.quote_id} data-testid="admin-quote-row" className="rounded-2xl bg-white border border-brand-line p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-brand-ink text-lg">{q.name}</span>
                      <span className="rounded-full bg-brand-surface2 text-brand-primary text-xs font-medium px-3 py-1">{q.service}</span>
                      {q.email_status === "not_sent" && (
                        <span className="rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1">email not delivered</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-brand-ink2">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-brand-primary"><Mail className="h-4 w-4" /> {q.email}</a>
                      <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-brand-primary"><Phone className="h-4 w-4" /> {q.phone}</a>
                    </div>
                    {q.message && <p className="mt-3 text-sm text-brand-ink2 max-w-2xl whitespace-pre-wrap">{q.message}</p>}
                  </div>
                  <span className="text-xs text-brand-ink2 whitespace-nowrap">{fmtDate(q.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));

  const logout = () => {
    localStorage.removeItem(KEY);
    setToken(null);
  };

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onAuth={setToken} />;
}
